import { Asset } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { Thumbnail } from '@/components/Thumbnail';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { getAssetFileName } from '@/utils/assets';
import { CheckCircle, Error as ErrorIcon, Search, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Tab,
  Tabs,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchAssetsByName } from '../../hooks/useSearchAssetsByName';
import { AssetMatch, BulkUploadData } from '../../types';
import { createAssetMatchingStepColumns } from '../columnDefinitions';
import { MetadataBulkDataGrid } from '../MetadataBulkDataGrid';

const useStyles = createUseStyles(theme => ({
  AssetMatchingStep_root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  AssetMatchingStep_content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '24px',
  },
  AssetMatchingStep_summary: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: theme.palette.background.default,
    borderRadius: '8px',
    flexShrink: 0,
  },
  AssetMatchingStep_tabContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  AssetMatchingStep_dataGridContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  AssetMatchingStep_actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    flexShrink: 0,
  },
  AssetMatchingStep_statusIcon: {
    marginRight: '8px',
  },
  AssetMatchingStep_progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
  },
}));

const TabPanel = ({ children, value, index }: { children?: React.ReactNode; index: number; value: number }) => {
  if (value !== index) {
    return;
  }

  return (
    <Box
      role='tabpanel'
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {children}
    </Box>
  );
};

const getStatusText = (match: AssetMatch) => {
  switch (match.status) {
    case 'matched': {
      return `Matched: ${match.selectedAsset?.path}`;
    }
    case 'asset_id_provided': {
      return `Matched: Asset ID ${match.assetId} will be used`;
    }
    case 'multiple_matches': {
      return `${match.searchResults.length} matches found - Select one`;
    }
    case 'no_match': {
      return 'No matching asset found';
    }
    case 'error': {
      return 'Search error occurred';
    }
    default: {
      return 'Searching...';
    }
  }
};

export const AssetMatchingStep = ({
  data,
  onNext,
  onBack,
  onDataChange,
}: {
  data?: BulkUploadData;
  onNext: () => void;
  onBack: () => void;
  onDataChange: (data: BulkUploadData) => void;
}) => {
  const c = useStyles();
  const [isSearching, setIsSearching] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedAssetDialog, setSelectedAssetDialog] = useState<
    | {
        assetName: string;
        matches: Asset[];
        currentSelection?: Asset;
      }
    | undefined
  >();
  const { searchAssetsByName } = useSearchAssetsByName();
  const { getPath } = useTenants();

  const getAssetUrl = (assetId: string) => {
    return getPath(`asset/${assetId}`);
  };

  useEffect(() => {
    if (data?.assetMatches.every(match => match.status === 'pending')) {
      performAssetSearch();
    }
  }, [data]);

  const performAssetSearch = async () => {
    if (!data) return;

    setIsSearching(true);
    const updatedMatches: AssetMatch[] = [];

    for (const match of data.assetMatches) {
      try {
        let status: AssetMatch['status'] = 'no_match';
        let selectedAsset: Asset | undefined;
        let searchResults: Asset[] = [];

        if (match.assetId) {
          status = 'asset_id_provided';
          selectedAsset = undefined;
          searchResults = [];
        } else {
          const fileName = getAssetFileName({ path: match.assetName });
          searchResults = await searchAssetsByName(fileName);

          const exactMatches = searchResults.filter(asset => asset.path === match.assetName);

          if (exactMatches.length === 1) {
            status = 'matched';
            selectedAsset = exactMatches[0];
          } else if (exactMatches.length > 1 || searchResults.length > 0) {
            status = 'multiple_matches';
          }
        }

        updatedMatches.push({
          ...match,
          searchResults,
          selectedAsset,
          assetId: selectedAsset?.id || match.assetId,
          status,
        });
      } catch {
        updatedMatches.push({
          ...match,
          searchResults: [],
          status: 'error',
        });
      }
    }

    const updatedData = {
      ...data,
      assetMatches: updatedMatches,
    };

    onDataChange(updatedData);
    setIsSearching(false);
  };

  const handleSelectAsset = (assetName: string, selectedAsset: Asset) => {
    if (!data) return;

    const updatedMatches = data.assetMatches.map(match =>
      match.assetName === assetName
        ? { ...match, selectedAsset, assetId: selectedAsset.id, status: 'matched' as const }
        : match
    );

    onDataChange({
      ...data,
      assetMatches: updatedMatches,
    });

    setSelectedAssetDialog(undefined);
  };

  const openAssetSelectionDialog = (assetName: string, matches: Asset[], currentSelection?: Asset) => {
    setSelectedAssetDialog({ assetName, matches, currentSelection });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!data) {
    return <Alert severity='error'>No data available. Please go back and upload a file.</Alert>;
  }

  const { assetMatches } = data;
  const matchedCount = assetMatches.filter(
    match => match.status === 'matched' || match.status === 'asset_id_provided'
  ).length;
  const multipleMatchesCount = assetMatches.filter(match => match.status === 'multiple_matches').length;
  const noMatchCount = assetMatches.filter(match => match.status === 'no_match').length;
  const errorCount = assetMatches.filter(match => match.status === 'error').length;

  const canProceed = matchedCount > 0 && multipleMatchesCount === 0;

  const getStatusIcon = (status: AssetMatch['status']) => {
    switch (status) {
      case 'matched': {
        return <CheckCircle color='success' className={c.AssetMatchingStep_statusIcon} />;
      }
      case 'asset_id_provided': {
        return <CheckCircle color='success' className={c.AssetMatchingStep_statusIcon} />;
      }
      case 'multiple_matches': {
        return <Warning color='warning' className={c.AssetMatchingStep_statusIcon} />;
      }
      case 'no_match': {
        return <ErrorIcon color='error' className={c.AssetMatchingStep_statusIcon} />;
      }
      case 'error': {
        return <ErrorIcon color='error' className={c.AssetMatchingStep_statusIcon} />;
      }
      default: {
        return <Search className={c.AssetMatchingStep_statusIcon} />;
      }
    }
  };

  const columns = createAssetMatchingStepColumns(
    getStatusIcon,
    getStatusText,
    openAssetSelectionDialog,
    getAssetUrl,
    data
  );

  const matchedMatches = assetMatches.filter(
    match => match.status === 'matched' || match.status === 'asset_id_provided'
  );
  const multipleMatches = assetMatches.filter(match => match.status === 'multiple_matches');
  const noMatches = assetMatches.filter(match => match.status === 'no_match' || match.status === 'error');

  const allRows = assetMatches.map((match, index) => ({
    id: `all-${index}`,
    name: match.assetName,
    ...match,
  }));

  const matchedRows = matchedMatches.map((match, index) => ({
    id: `matched-${index}`,
    name: match.assetName,
    ...match,
  }));

  const multipleMatchesRows = multipleMatches.map((match, index) => ({
    id: `multiple-${index}`,
    name: match.assetName,
    ...match,
  }));

  const noMatchRows = noMatches.map((match, index) => ({
    id: `no-match-${index}`,
    name: match.assetName,
    ...match,
  }));

  return (
    <Box className={c.AssetMatchingStep_root}>
      <Box className={c.AssetMatchingStep_content}>
        <HeaderText gutterBottom>Asset Matching</HeaderText>
        {isSearching && (
          <Box className={c.AssetMatchingStep_progressContainer}>
            <CircularProgress size={24} />
            <BodyText>Searching for assets by name...</BodyText>
          </Box>
        )}
        <Box className={c.AssetMatchingStep_summary}>
          <HeaderText gutterBottom>Matching Summary</HeaderText>
          <Box display='flex' gap={2} flexWrap='wrap'>
            <Chip icon={<CheckCircle />} label={`${matchedCount} Matched`} color='success' variant='outlined' />
            <Chip
              icon={<Warning />}
              label={`${multipleMatchesCount} Multiple Matches`}
              color='warning'
              variant='outlined'
            />
            <Chip icon={<ErrorIcon />} label={`${noMatchCount} No Match`} color='error' variant='outlined' />
            {errorCount > 0 && (
              <Chip icon={<ErrorIcon />} label={`${errorCount} Errors`} color='error' variant='outlined' />
            )}
          </Box>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`All (${assetMatches.length})`} />
            <Tab label={`Matched (${matchedMatches.length})`} />
            <Tab label={`Multiple Matches (${multipleMatches.length})`} />
            <Tab label={`No Match (${noMatches.length})`} />
          </Tabs>
        </Box>
        <Box className={c.AssetMatchingStep_tabContent}>
          <TabPanel value={tabValue} index={0}>
            <Box className={c.AssetMatchingStep_dataGridContainer}>
              <MetadataBulkDataGrid columns={columns} rows={allRows} rowCount={allRows.length} />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Box className={c.AssetMatchingStep_dataGridContainer}>
              <MetadataBulkDataGrid columns={columns} rows={matchedRows} rowCount={matchedRows.length} />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <Box className={c.AssetMatchingStep_dataGridContainer}>
              <MetadataBulkDataGrid
                columns={columns}
                rows={multipleMatchesRows}
                rowCount={multipleMatchesRows.length}
              />
            </Box>
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <Box className={c.AssetMatchingStep_dataGridContainer}>
              <MetadataBulkDataGrid columns={columns} rows={noMatchRows} rowCount={noMatchRows.length} />
            </Box>
          </TabPanel>
        </Box>
        {!canProceed && (
          <Alert severity='warning' sx={{ marginBottom: 2 }}>
            {multipleMatchesCount > 0 && 'Please resolve all multiple matches before proceeding.'}
            {matchedCount === 0 && 'No assets were matched. Please check your asset names and try again.'}
          </Alert>
        )}
      </Box>
      <Box className={c.AssetMatchingStep_actions}>
        <Button variant='outlined' onClick={onBack}>
          Back
        </Button>
        <Button variant='contained' onClick={onNext} disabled={!canProceed || isSearching}>
          Continue
        </Button>
      </Box>
      <Dialog open={!!selectedAssetDialog} onClose={() => setSelectedAssetDialog(undefined)} maxWidth='md' fullWidth>
        <DialogTitle>Select Asset for &quot;{selectedAssetDialog?.assetName}&quot;</DialogTitle>
        <DialogContent>
          <BodyText color='textSecondary' sx={{ marginBottom: 2 }}>
            Multiple assets were found with this filename. Please select the correct one:
          </BodyText>
          <List>
            {selectedAssetDialog?.matches.map(asset => {
              const isSelected = selectedAssetDialog.currentSelection?.id === asset.id;
              return (
                <ListItem key={asset.id} disablePadding>
                  <ListItemButton onClick={() => handleSelectAsset(selectedAssetDialog.assetName, asset)}>
                    <ListItemIcon>
                      <Radio checked={isSelected} />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Thumbnail asset={asset} size={42} noLink />
                      <ListItemText
                        primary={asset.path}
                        secondary={`ID: ${asset.id} | Created: ${new Date(asset.createdAt).toLocaleDateString()}`}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedAssetDialog(undefined)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
